import { useEffect, useLayoutEffect } from 'react'

import { TAccountsSelectOptionData } from 'App/components/common/AccountsSelect/AccountsSelectOption/AccountsSelectOption'
import { useQuery } from 'services/query'
import { TChannelVariant } from 'interfaces'
import { getChatRoomById } from 'api'
import { updateChatRoomAction, useAppDispatch, useAppSelector } from 'store'
import { getEventChannelMembers } from 'api/events'

type TVariant = Extract<TChannelVariant, 'chat' | 'news'>

export type TUseEventMembersRequest = {
  variant: TVariant
  eventId: string
  setChannelMembers: (acc: TAccountsSelectOptionData[], prefetched?: boolean) => void

  channelId?: string
  isEdit?: boolean
}

const CHANNEL_KEY_MAP = new Map<TVariant, string>()
  .set('news', 'news-room-accounts')
  .set('chat', 'text-room-accounts')

export const useEventMembersRequest = ({
  isEdit,
  eventId,
  variant,
  setChannelMembers,
  channelId = ''
}: TUseEventMembersRequest) => {
  const dispatch = useAppDispatch()

  const organizerId = useAppSelector((state) => state.events.data.list[eventId].organizerId)
  const chatRoomsState = useAppSelector((state) => state.chat.chatRooms)

  const {
    data: accounts = [],
    refetch,
    isInitialLoading: isLoading
  } = useQuery({
    queryKey: [CHANNEL_KEY_MAP.get(variant), channelId],
    queryFn: () => getEventChannelMembers(channelId),
    select: (data) =>
      data.results.map<TAccountsSelectOptionData>(
        ({ id, displayUserName, firstName, lastName, type }) => ({
          id,
          displayUserName,
          firstName,
          lastName,
          type
        })
      ),
    enabled: false,
    cacheTime: 0
  })

  useQuery({
    queryKey: ['update-channel-room-before-edit'],
    queryFn: () => getChatRoomById(channelId ?? ''),
    enabled: isEdit,
    onSuccess: (data) => {
      if (channelId) {
        dispatch(
          updateChatRoomAction({
            ids: chatRoomsState.ids,
            list: {
              ...chatRoomsState.list,
              [channelId]: data
            }
          })
        )
      }
    }
  })

  useLayoutEffect(() => {
    if (isEdit) {
      refetch()
    }
  }, [isEdit, refetch])

  useEffect(() => {
    if (isEdit && accounts?.length) {
      setChannelMembers(
        accounts.filter((item) => item.id !== organizerId),
        true
      )
    }
  }, [isEdit, organizerId, accounts, setChannelMembers])

  return {
    accounts,
    isLoading
  }
}
